import { Inject, Injectable } from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';
import { Actions, ofType, createEffect, concatLatestFrom } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { map, tap, switchMap, first, of } from 'rxjs';

import { RgpdModalComponent } from '../modals/rgpd/rgpd.component';
import { TermsConditionsDocumentsService } from '../services/terms-conditions-documents.service';

import { OpenDocument, OpenPrivacyPolicy } from './terms-conditions.actions';

import { CommonWindow } from '@core/domain/models/common-window.model';
import { getCmsConfigValue, getStoreAndLanguage } from '@core/infrastructure/state/core.selectors';
import { StaticResourceService } from '@core/services/static-resource.service';
import { TimestampService } from '@core/services/timestamp.service';
import { RgpdService } from '@shared/services/rgpd.service';
import { DialogMargins, DialogOptions, DialogService, DialogSizes } from '@ui/dialog/dialog.service';

@Injectable()
export class TermsConditionsEffects {
    constructor(
        private readonly actions$: Actions,
        private readonly store: Store,
        private readonly staticResourceService: StaticResourceService,
        private readonly termsConditionsDocumentsService: TermsConditionsDocumentsService,
        private readonly timestampService: TimestampService,
        private readonly rgpdService: RgpdService,
        private readonly dialogService: DialogService,
        @Inject(WINDOW) private readonly window: CommonWindow,
    ) { }

    public openDocument$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OpenDocument),
            concatLatestFrom(() => this.store.select(getStoreAndLanguage)),
            map(([{ name }, { storeId, languageId }]) => {
                const documentRoute = this.termsConditionsDocumentsService.getDocumentRoute(name);
                const timestamp = this.timestampService.getTs();

                return this.staticResourceService.getStatic()
                    + documentRoute.part1
                    + storeId
                    + documentRoute.part2
                    + languageId
                    + '.pdf?'
                    + timestamp;
            }),
            tap((pdfUrl) => this.window.open(pdfUrl, '_blank'))
        );
    }, { dispatch: false });

    public openPrivacyPolicy$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(OpenPrivacyPolicy),
            switchMap((action) =>
                this.store.select(getCmsConfigValue('popup-politicasPrivacidad')).pipe(
                    switchMap((isPopup) => {
                        if (!isPopup) {
                            this.store.dispatch(OpenDocument({ name: 'privacyPolicy' }));
                            return of([]);
                        }
                        return this.rgpdService.getRGPDContent(action.layer || 'layer_1').pipe(first());
                    }),
                    tap((content) => {
                        if (content && Object.keys(content).length > 0) {
                            this.dialogService.openModalFromComponent(
                                'dev.rgpdModal.title',
                                RgpdModalComponent,
                                content,
                                DialogSizes.SIZE_S,
                                DialogMargins.MARGIN_40,
                                [DialogOptions.FULL_MOBILE]
                            );
                        }
                    }),
                )),
        );
    }, { dispatch: false });
}
